import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { useNavigate } from "react-router-dom";
import { License, User, ExtendedUser, Project } from "../../store/types";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { formatPhone } from "../../hooks/validation";
import { getLicenseType } from "../../hooks/useLicenseType";
import {
  Container,
  ContainerXScroll,
} from "../../components/Container/Container";
import Card from "../../components/Card/Card";
import CustomInputText from "../../components/CustomInput/CustomInputText";
import CustomDatatable from "../../components/CustomDatatable/CustomDatatable";
import {
  companyLicenseColumns,
  getLicenseContextMenuItems,
  getLicenseMobileActions,
} from "../../components/DatatableTemplates/LicenseTableTemplates";
import { companyUserColumns } from "../../components/DatatableTemplates/UserTableTemplates";
import AddLicenseDialogButton from "../../components/DialogButton/AddLicenseDialogButton";
import AddUserDialogButton from "../../components/DialogButton/AddUserDialogButton";
import CompanyDialogs from "../../components/Dialogs/CompanyDialogs";
import LicenseDialogs from "../../components/Dialogs/LicenseDialogs";
import UserDialogs from "../../components/Dialogs/UserDialogs";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { H2 } from "../../styles/Typography";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import {
  IconUserFilled,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
  IconDocFilled,
  IconDocCheckFilled,
  IconDocWarnFilled,
  IconDocErrorFilled,
  IconArrowLeft,
  IconSearch,
  IconEdit,
  IconDelete,
} from "../../assets/icons/Icon";
import {
  ContainerFlex,
  ContainerColumnFlex,
  CardsColumnContainer,
  DatatablesColumn,
  FlexColumn,
  CardsFlex,
  ButtonContainer,
  ButtonLabel,
  SearchContainer,
} from "./CompanyDetails.styles";
import {
  FlexLineJustify,
  FlexLineGapSmall,
  PageName,
  Label,
  ButtonBox,
} from "../../styles/PageLayoutStyles.styles";
import { Spinner } from "../CRUDPages/CRUDPages.styles";

const CompanyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)") || false;

  const licenseTableRef = useRef<DataTable<License[]>>(null);
  const userTableRef = useRef<DataTable<ExtendedUser[]>>(null);
  const [licenseSearchValue, setLicenseSearchValue] = useState<string>("");
  const [userSearchValue, setUserSearchValue] = useState<string>("");
  const [licenseList, setLiscenseList] = useState<License[]>([]);
  const [userList, setUserList] = useState<ExtendedUser[]>([]);

  const [totalLicenses, setTotalLicenses] = useState(0);
  const [activeLicenses, setActiveLicenses] = useState(0);
  const [expiringLicenses, setExpiringLicenses] = useState(0);
  const [inactiveLicenses, setInactiveLicenses] = useState(0);

  const [selectedLicense, setSelectedLicense] = useState<License | null>(null);
  const [selectedUser, setSelectedUser] = useState<ExtendedUser | null>(null);
  const [licenseDialogVisible, setLicenseDialogVisible] =
    useState<boolean>(false);
  const [licenseDialogType, setLicenseDialogType] = useState<string>("");
  const [companyDialogVisible, setCompanyDialogVisible] =
    useState<boolean>(false);
  const [companyDialogType, setCompanyDialogType] = useState<string>("");
  const [userDialogVisible, setUserDialogVisible] = useState<boolean>(false);
  const [userDialogType, setUserDialogType] = useState<string>("");

  const {
    companiesEP,
    handleUpdateCompany,
    handleDeleteCompany,
    toastRef: companyToastRef,
    refetchData,
    companiesDropdownOpts,
  } = useCompaniesAPI();
  const { projects, projectsDropdownOpts } = useProjectsAPI();
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicense,
    toastRef,
  } = useLicensesAPI();
  const {
    handlePostUser,
    handleUpdateUser,
    handleDeleteUser,
    toastRef: userToastRef,
  } = useUsersAPI();

  const {
    data: company,
    refetch: refetchCompany,
    isLoading,
  } = useGetQuery(`${companiesEP}/${id}`, {
    refetchOnMountOrArgChange: true,
    skip: !id || !companiesEP,
  });

  const { data: licenses, refetch: refetchLicenses } = useGetQuery(
    `${companiesEP}/${id}/licenses`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !companiesEP,
    }
  );

  const { data: users, refetch: refetchUsers } = useGetQuery(
    `${companiesEP}/${id}/members`,
    {
      refetchOnMountOrArgChange: true,
      skip: !id || !companiesEP,
    }
  );

  useEffect(() => {
    if (licenses && company && users) {
      const enriched = licenses.map((license: License) => {
        const companyName = company?.name;
        const licenseType = getLicenseType(license.type);
        const projectName =
          projects?.find((p: Project) => p.id === license.projectId)?.name ??
          license.projectId;
        const userName = license.userShortName ?? "—";

        let validityDays = 0;
        if (license.startDate && license.expirationDate) {
          const start = new Date(license.startDate);
          const end = new Date(license.expirationDate);
          validityDays = Math.max(
            0,
            (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
          );
        }

        let remainingDays = 0;
        if (license.expirationDate) {
          const now = new Date();
          const exp = new Date(license.expirationDate);
          remainingDays =
            (exp.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
        }

        return {
          ...license,
          companyName,
          licenseType,
          projectName,
          userName,
          _validityDays: validityDays,
          _timeRemainingDays: remainingDays,
        };
      });
      setLiscenseList(enriched);

      const now = new Date();
      let total = licenses.length;
      let active = 0;
      let expiring = 0;
      let inactive = 0;

      licenses.forEach((license: License) => {
        const isActive = license.isEnabled;
        const expDate = license.expirationDate
          ? new Date(license.expirationDate)
          : null;
        const isExpired = expDate && expDate < now;
        let daysRemaining = expDate
          ? (expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
          : Infinity;

        if (isActive && !isExpired) {
          active++;
          if (daysRemaining < 30) expiring++;
        } else {
          inactive++;
        }
      });

      setTotalLicenses(total);
      setActiveLicenses(active);
      setExpiringLicenses(expiring);
      setInactiveLicenses(inactive);

      const usersWithLicenses = users.map((user: User) => {
        const userLicenseCount = licenses.filter(
          (license: License) => license.userId === user.id
        ).length;
        return {
          ...user,
          fullName: `${user.lastName ?? ""} ${user.name ?? ""} ${
            user.patronymic ?? ""
          }`.trim(),
          licenses: userLicenseCount,
        };
      });
      setUserList(usersWithLicenses);
    } else {
      setLiscenseList([]);
      setUserList([]);
      setTotalLicenses(0);
      setActiveLicenses(0);
      setExpiringLicenses(0);
      setInactiveLicenses(0);
    }
  }, [licenses, company, projects, users]);

  const filteredLicenseData = useMemo(() => {
    if (!licenseSearchValue.trim() || !licenseList) return licenseList;
    const lowerSearch = licenseSearchValue.toLowerCase();
    return licenseList.filter((license) => {
      const lic = license as any;
      return (
        lic.projectName?.toLowerCase().includes(lowerSearch) ||
        lic.licenseType?.toLowerCase().includes(lowerSearch) ||
        lic.isEnabled?.toString().includes(lowerSearch) ||
        lic.startDate?.toString().includes(lowerSearch) ||
        lic.expirationDate?.toString().includes(lowerSearch) ||
        lic.validityPeriod?.toLowerCase().includes(lowerSearch) ||
        lic.timeRemaining?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [licenseList, licenseSearchValue]);

  const filteredUserData = useMemo(() => {
    if (!userSearchValue.trim() || !userList) return userList;
    const lowerSearch = userSearchValue.toLowerCase();
    return userList.filter((user) => {
      const u = user as any;
      return (
        u.fullName?.toLowerCase().includes(lowerSearch) ||
        u.licenses?.toString().includes(lowerSearch) ||
        u.phone?.toLowerCase().includes(lowerSearch) ||
        u.email?.toLowerCase().includes(lowerSearch)
      );
    });
  }, [userList, userSearchValue]);

  const handleEnableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=enable&id=${license.id}`);
    } else {
      setLicenseDialogType("enable");
      setLicenseDialogVisible(true);
    }
  };

  const handleDisableLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=disable&id=${license.id}`);
    } else {
      setLicenseDialogType("disable");
      setLicenseDialogVisible(true);
    }
  };

  const handleAddUser = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=addUser&id=${license.id}`);
    } else {
      setLicenseDialogType("addUser");
      setLicenseDialogVisible(true);
    }
  };

  const contextMenuItems = useMemo(
    () =>
      getLicenseContextMenuItems(
        selectedLicense,
        handleAddUser,
        handleEnableLicense,
        handleDisableLicense
      ),
    [selectedLicense, handleAddUser, handleEnableLicense, handleDisableLicense]
  );

  const mobileActions = useMemo(
    () => getLicenseMobileActions(handleEnableLicense, handleDisableLicense),
    [handleEnableLicense, handleDisableLicense]
  );

  const handleViewLicense = (license: License) => {
    navigate(`/licenses/${license.id}`);
  };

  const handleViewUser = (user: ExtendedUser) => {
    navigate(`/users/${user.id}`);
  };

  const handleSelectionChangeLicense = (selected: License | null) => {
    setSelectedLicense(selected);
  };

  const handleSelectionChangeUser = (selected: ExtendedUser | null) => {
    setSelectedUser(selected);
  };

  const resetAllFilters = () => {
    if (licenseTableRef.current) {
      licenseTableRef.current.reset();
    }
    setLicenseSearchValue("");
  };

  const editLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=edit&id=${license.id}`);
    } else {
      setLicenseDialogType("edit");
      setLicenseDialogVisible(true);
    }
  };

  const deleteLicense = (license: License) => {
    setSelectedLicense(license);

    if (isMobile) {
      navigate(`/licenses/form?mode=delete&id=${license.id}`);
    } else {
      setLicenseDialogType("delete");
      setLicenseDialogVisible(true);
    }
  };

  const editUser = (user: ExtendedUser) => {
    setSelectedUser(user);

    if (isMobile) {
      navigate(`/users/form?mode=edit&id=${user.id}`);
    } else {
      setUserDialogType("edit");
      setUserDialogVisible(true);
    }
  };

  const deleteUser = (user: ExtendedUser) => {
    setSelectedUser(user);

    if (isMobile) {
      navigate(`/users/form?mode=delete&id=${user.id}`);
    } else {
      setUserDialogType("delete");
      setUserDialogVisible(true);
    }
  };

  const editCompany = () => {
    if (isMobile) {
      navigate(`/companies/form?mode=edit&id=${company.id}`);
    } else {
      setCompanyDialogType("edit");
      setCompanyDialogVisible(true);
    }
  };

  const deleteCompany = () => {
    if (isMobile) {
      navigate(`/companies/form?mode=delete&id=${company.id}`);
    } else {
      setCompanyDialogType("delete");
      setCompanyDialogVisible(true);
    }
  };

  const deleteCompanyAPI = async (): Promise<boolean> => {
    try {
      let success = await handleDeleteCompany(company);
      if (success) {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    } finally {
      setTimeout(() => {
        navigate(-1);
      }, 2000);
      refetchData();
    }
  };

  if (isLoading || !company)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <ContainerFlex>
      <Toast ref={toastRef} position="bottom-center" />
      <Toast ref={companyToastRef} position="bottom-center" />
      <Toast ref={userToastRef} position="bottom-center" />
      <ContainerColumnFlex>
        <CardsColumnContainer>
          <Container>
            <FlexLineJustify>
              <H2>Данные компании</H2>
              <ButtonBox>
                <Button text onClick={editCompany}>
                  <IconEdit size={16} />
                </Button>
                <Button text onClick={deleteCompany}>
                  <IconDelete size={16} />
                </Button>
              </ButtonBox>
            </FlexLineJustify>
            <FlexColumn>
              <StyledIconLine
                icon={<IconCompanyFilled size={20} />}
                text={company?.name ?? "Нет данных"}
              />
              <StyledIconLine
                icon={<IconMailFilled size={20} />}
                text={company?.mail ?? "Нет данных"}
              />
              <StyledIconLine
                icon={<IconPhoneFilled size={20} />}
                text={
                  company.phone ? formatPhone(company!.phone) : "Нет данных"
                }
              />
            </FlexColumn>
          </Container>

          <Container>
            <H2>Статистика</H2>
            <FlexColumn>
              <CardsFlex>
                <Card
                  icon={<IconDocFilled size={24} />}
                  header={totalLicenses.toString()}
                  title="Количество лицензий"
                />
                <Card
                  icon={<IconDocCheckFilled size={24} />}
                  header={activeLicenses.toString()}
                  title="Активные лицензии"
                />
              </CardsFlex>
              <CardsFlex>
                <Card
                  icon={<IconDocWarnFilled size={24} />}
                  header={expiringLicenses.toString()}
                  title="Истекающие лицензии"
                />
                <Card
                  icon={<IconDocErrorFilled size={24} />}
                  header={inactiveLicenses.toString()}
                  title="Неактивные лицензии"
                />
              </CardsFlex>
              <Card
                icon={<IconUserFilled size={24} />}
                header={userList ? userList.length.toString() : "0"}
                title="Количество пользователей"
              />
            </FlexColumn>
          </Container>
        </CardsColumnContainer>

        <ButtonContainer>
          <Button outlined onClick={() => navigate(-1)}>
            <IconArrowLeft size={16} />
            <ButtonLabel>Назад</ButtonLabel>
          </Button>
        </ButtonContainer>
      </ContainerColumnFlex>

      <DatatablesColumn>
        <ContainerXScroll>
          <FlexLineJustify>
            <PageName>
              <H2>Лицензии</H2>
            </PageName>
            <FlexLineGapSmall>
              <AddLicenseDialogButton
                company={company}
                projectsOptions={projectsDropdownOpts}
                handleActivateLicense={handleActivateLicense}
                handleDeactivateLicense={handleDeactivateLicense}
                handleAddLicense={handlePostLicense}
                handleEditLicense={handleUpdateLicense}
                handleEditLicenseUser={handleUpdateLicenseUser}
                handleDeleteLicense={handleDeleteLicense}
                refetchData={refetchLicenses}
                licensesEP={licensesEP}
                isMobile={isMobile}
              />
              <Button
                icon="pi pi-filter-slash"
                onClick={resetAllFilters}
                outlined
              >
                <Label>Сбросить</Label>
              </Button>
              <SearchContainer>
                <CustomInputText
                  value={licenseSearchValue}
                  setValue={setLicenseSearchValue}
                  placeholder="Поиск..."
                  icon={<IconSearch size={20} />}
                />
              </SearchContainer>
            </FlexLineGapSmall>
          </FlexLineJustify>

          <CustomDatatable<License>
            ref={licenseTableRef}
            data={filteredLicenseData ?? []}
            columns={companyLicenseColumns}
            onView={handleViewLicense}
            onEdit={editLicense}
            onDelete={deleteLicense}
            onRowDoubleClick={handleViewLicense}
            selected={selectedLicense}
            onSelectionChange={handleSelectionChangeLicense}
            getRowId={(license) => license.id}
            extraContextMenuItems={contextMenuItems}
            hideViewButton
            mobileActions={mobileActions}
            sortable
          />
          <LicenseDialogs
            visible={licenseDialogVisible}
            onHide={() => setLicenseDialogVisible(false)}
            dialogType={licenseDialogType}
            license={selectedLicense}
            enrichedLicense={selectedLicense}
            handleActivateLicense={handleActivateLicense}
            handleDeactivateLicense={handleDeactivateLicense}
            handleAddLicense={handlePostLicense}
            handleEditLicense={handleUpdateLicense}
            handleEditLicenseUser={handleUpdateLicenseUser}
            handleDeleteLicense={handleDeleteLicense}
            refetchData={refetchLicenses}
            companiesOptions={companiesDropdownOpts}
            usersList={users}
            projectsOptions={projectsDropdownOpts}
            licensesEP={licensesEP}
          />
        </ContainerXScroll>

        <ContainerXScroll>
          <FlexLineJustify>
            <PageName>
              <H2>Пользователи</H2>
            </PageName>
            <FlexLineGapSmall>
              <AddUserDialogButton
                company={company}
                handleAddUser={handlePostUser}
                handleEditUser={handleUpdateUser}
                handleDeleteUser={handleDeleteUser}
                refetchData={refetchUsers}
                isMobile={isMobile}
              />
              <SearchContainer>
                <CustomInputText
                  value={userSearchValue}
                  setValue={setUserSearchValue}
                  placeholder="Поиск..."
                  icon={<IconSearch size={20} />}
                />
              </SearchContainer>
            </FlexLineGapSmall>
          </FlexLineJustify>

          <CustomDatatable<ExtendedUser>
            ref={userTableRef}
            data={filteredUserData ?? []}
            columns={companyUserColumns}
            onView={handleViewUser}
            onEdit={editUser}
            onDelete={deleteUser}
            onRowDoubleClick={handleViewUser}
            selected={selectedUser}
            onSelectionChange={handleSelectionChangeUser}
            getRowId={(user) => user.id}
            hideViewButton
            sortable
          />
          <UserDialogs
            visible={userDialogVisible}
            onHide={() => setUserDialogVisible(false)}
            dialogType={userDialogType}
            user={selectedUser}
            handleEditUser={handleUpdateUser}
            handleDeleteUser={handleDeleteUser}
            refetchData={refetchUsers}
            companiesOptions={companiesDropdownOpts}
          />
        </ContainerXScroll>
      </DatatablesColumn>

      <CompanyDialogs
        visible={companyDialogVisible}
        onHide={() => setCompanyDialogVisible(false)}
        dialogType={companyDialogType}
        company={company}
        handleEditCompany={handleUpdateCompany}
        handleDeleteCompany={deleteCompanyAPI}
        refetchData={refetchCompany}
      />
    </ContainerFlex>
  );
};

export default CompanyDetails;
